import React, { Fragment } from 'react';
import isEqual from 'react-fast-compare';
import { useDispatch } from 'react-redux';

import Panel from '../../../../components/outline/Panel';
import Table from '../../../../components/_table/Table';
import DateRangePicker from '../../../../components/datePicker/DateRangePicker';
import { DATE_FORMAT } from '../../../../constants/service/common/utilConstants';
import Button from '../../../../components/Button';
import { reDirectPage } from '../../../../lib/common';
import { WIDGET_CREATE_URL_PATH } from '../../../../constants/service/common/urlConstants';
import {
  PLACEHOLDER_TEXT_WIDGET_EXPOSURE_PERIOD_DATE,
  WIDGET_EXPOSURE_TYPE,
} from '../../../../constants/service/app/widgetConstants';
import { columns } from './columns';
import {
  WidgetAllListPagination,
  WidgetAllListType,
  WidgetAllListRequest,
} from '../WidgetAllListWrap/WidgetAllListWrap';
import { ValueOf } from '../../../../types/common/utils';
import { DateRageType } from '../../../../types/common/date';
import { widgetActions } from '../../../../store/modules/widget';
import './WidgetAllList.scss';

interface WidgetAllListProps {
  selectedWidgetIdList: number[];
  pagination: WidgetAllListPagination;
  widgetList: WidgetAllListType['widgetList']['list'];
  getWidgetPreviewData: (payload: { widgetId: number }) => void;
  getList: (data: WidgetAllListRequest) => Promise<void>;
  onChangeBulkWidgetPeriod: (widgetExposureType: ValueOf<typeof WIDGET_EXPOSURE_TYPE>) => void;
  saveWidgetAllList: () => Promise<void>;
  widgetExposurePeriodBatchInfo: WidgetAllListType['widgetExposurePeriodBatchInfo'];
  setInitialWidgetAllList: () => void;
  originalWidgetList: WidgetAllListType['originalWidgetList'];
  deleteWidget: (widgetId: number) => Promise<void>;
}

const WidgetAllList = (props: WidgetAllListProps): JSX.Element => {
  const {
    selectedWidgetIdList,
    pagination,
    widgetList,
    getWidgetPreviewData,
    getList,
    onChangeBulkWidgetPeriod,
    saveWidgetAllList,
    widgetExposurePeriodBatchInfo,
    setInitialWidgetAllList,
    originalWidgetList,
    deleteWidget,
  } = props;
  const dispatch = useDispatch();

  const handleChangePeriodBatchInfo = (period: DateRageType): void => {
    dispatch(widgetActions.setWidgetPeriodBatchInfo(period));
  };

  const handleSelectedWidgetIdList = (selectedWidgetIdList: number[]): void => {
    dispatch(widgetActions.setSelectedWidgetIdList(selectedWidgetIdList));
  };

  /**
   *  위젯 변경 사항 체크
   */
  const isUpdateList = !isEqual(widgetList, originalWidgetList);

  return (
    <div className="widget-all-list">
      <Panel>
        <div className="panel-header">
          <div className="title">위젯 전체 리스트</div>
          <div className="btn-area">
            {
              /**
               *  위젯 전체 리스트가 변경이 있을 때만 초기화, 저장 버튼을 노출함
               */
              isUpdateList ? (
                <Fragment>
                  <Button text="초기화" onClick={setInitialWidgetAllList} />
                  <Button text="저장" color="primary" onClick={saveWidgetAllList} />
                </Fragment>
              ) : null
            }
            <Button
              color="primary"
              text="위젯 생성하기"
              onClick={() => reDirectPage({ path: WIDGET_CREATE_URL_PATH })}
            />
          </div>
        </div>

        <div className="panel-body">
          <div className="all-widget-btn-area">
            <div className="button-form">
              <div className="label">노출기간</div>

              <div className="button-list">
                <DateRangePicker
                  value={widgetExposurePeriodBatchInfo}
                  showTime={true}
                  startText={PLACEHOLDER_TEXT_WIDGET_EXPOSURE_PERIOD_DATE}
                  endText={PLACEHOLDER_TEXT_WIDGET_EXPOSURE_PERIOD_DATE}
                  outFormat={DATE_FORMAT.FULL_DASH_FORMAT}
                  btnTextFormat={DATE_FORMAT.FULL_DASH_FORMAT}
                  format={DATE_FORMAT.FULL_DASH_FORMAT}
                  onSelect={handleChangePeriodBatchInfo}
                />

                <div className="apply-button-list">
                  <Button
                    color="primary"
                    onClick={() => onChangeBulkWidgetPeriod(WIDGET_EXPOSURE_TYPE.DIRECT_SETTING)}
                    text="기간 적용"
                  />

                  <Button
                    color="light"
                    onClick={() => onChangeBulkWidgetPeriod(WIDGET_EXPOSURE_TYPE.INFINITE)}
                    text="무기한 설정"
                  />
                </div>
              </div>
            </div>
          </div>

          <Table
            columns={() =>
              columns({
                getWidgetPreviewData,
                deleteWidget,
              })
            }
            rowKey={(record: { id: number }): number => record.id}
            pagination={pagination}
            list={widgetList}
            size="small"
            scroll={{ x: true }}
            bordered={true}
            getList={getList}
            onCheck={handleSelectedWidgetIdList}
            selectedList={selectedWidgetIdList}
          />
        </div>
      </Panel>
    </div>
  );
};

export default WidgetAllList;
